import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2, Ticket, CreditCard, RefreshCw, MapPin, CalendarDays, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import SavedPaymentMethods from '../components/SavedPaymentMethods';
import ClassPassPurchaseDialog from '../components/ClassPassPurchaseDialog';
import { apiGet, apiPut } from '../api';
import { formatApiError } from '../lib/apiError';
import { formatActivity } from '../lib/formatActivity';

export const MembershipsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [classPasses, setClassPasses] = useState([]);
  const [availablePasses, setAvailablePasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedCards, setSavedCards] = useState([]);
  const [showAutoRenewDialog, setShowAutoRenewDialog] = useState(false);
  const [autoRenewPass, setAutoRenewPass] = useState(null);
  const [processingAutoRenew, setProcessingAutoRenew] = useState(false);

  // The "open purchase dialog" for the shared <ClassPassPurchaseDialog />
  const [purchasePass, setPurchasePass] = useState(null);

  // Iter-70: block membership checkout when the client has no saved cards.
  // Memberships need a card-on-file for auto-renewal (Ryft MIT/Unscheduled).
  // Cards are only saveable via Profile > Billing, so we prompt the user
  // there instead of letting them fail mid-checkout.
  const [membershipNeedsCard, setMembershipNeedsCard] = useState(null);

  const handlePurchaseClick = (pass) => {
    if (pass.is_membership && (savedCards || []).length === 0) {
      setMembershipNeedsCard(pass);
      return;
    }
    setPurchasePass({
      ...pass,
      // /api/available-class-passes returns `passes_included`; the shared
      // dialog expects `passes_count` (same shape as /api/class-passes).
      passes_count: pass.passes_count ?? pass.passes_included,
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [passesRes, availableRes, cardsRes] = await Promise.all([
        apiGet('/api/user-class-passes'),
        apiGet('/api/available-class-passes'),
        apiGet('/api/payment-methods').catch(() => [])
      ]);
      setClassPasses(passesRes);
      setAvailablePasses(availableRes);
      setSavedCards(cardsRes);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoRenew = async (pass, enabled) => {
    if (enabled && savedCards.length === 0) {
      // Need to add a card first
      setAutoRenewPass(pass);
      setShowAutoRenewDialog(true);
      return;
    }

    setProcessingAutoRenew(true);
    try {
      const paymentMethodId = enabled ? (savedCards.find(c => c.is_default)?.id || savedCards[0]?.id) : null;
      const query = { enabled: enabled.toString() };
      if (paymentMethodId) query.payment_method_id = paymentMethodId;

      await apiPut('/api/user-class-passes/{pass_id}/auto-renew', { params: { pass_id: pass.id }, query });
      toast.success(enabled ? 'Auto-renewal enabled' : 'Auto-renewal disabled');
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update auto-renewal'));
    } finally {
      setProcessingAutoRenew(false);
    }
  };

  const handleCardAdded = () => {
    // Refresh cards and enable auto-renewal
    fetchData();
    setShowAutoRenewDialog(false);
    if (autoRenewPass) {
      // Auto-enable after card added
      setTimeout(() => {
        handleToggleAutoRenew(autoRenewPass, true);
        setAutoRenewPass(null);
      }, 500);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const activeClassPasses = classPasses.filter(p => p.status === 'active');
  const expiredClassPasses = classPasses.filter(p => p.status !== 'active');

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">My ClassPasses</h1>
          <p className="text-muted-foreground">View and manage your class passes</p>
        </div>

        {/* Active ClassPasses */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" />
              Active ClassPasses ({activeClassPasses.length})
            </CardTitle>
            <CardDescription>Use these to book classes at their respective studios</CardDescription>
          </CardHeader>
          <CardContent>
            {activeClassPasses.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {activeClassPasses.map((pass, i) => {
                  const daysLeft = Math.max(0, Math.ceil((new Date(pass.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)));
                  const passesUsed = pass.passes_total - pass.passes_remaining;
                  const progress = (passesUsed / pass.passes_total) * 100;
                  const neverExpires = pass.validity_days === 0 || daysLeft > 365 * 5;
                  const isMembership = pass.is_membership;
                  
                  return (
                    <Card key={pass.id} className="border-primary/20" data-testid={`classpass-${i}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge>{formatActivity(pass.activity_type)}</Badge>
                            {isMembership && (
                              <Badge variant="secondary" className="text-blue-600">
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Membership
                              </Badge>
                            )}
                          </div>
                          <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                            Active
                          </Badge>
                        </div>
                        <CardTitle className="text-lg">{pass.class_pass_name}</CardTitle>
                        {/* Iter-46ao: surface the studio + purchase context so users can see
                            where this pass was bought from and when. The GET /user-class-passes
                            response now enriches `studio_name` + `studio_slug`. */}
                        {(pass.studio_name || pass.purchase_date || pass.purchased_at) && (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground pt-1">
                            {pass.studio_name && (
                              pass.studio_slug ? (
                                // Bug R28 fix (Iter-65): use react-router
                                // Link (SPA nav) instead of <a href> so a
                                // missing slug can't produce `/studio/` and
                                // fall through the catch-all Route to home.
                                <Link
                                  to={`/studio/${pass.studio_slug}`}
                                  className="flex items-center gap-1 hover:text-primary transition-colors"
                                  data-testid={`classpass-studio-${i}`}
                                >
                                  <MapPin className="w-3 h-3" />
                                  {pass.studio_name}
                                </Link>
                              ) : (
                                <span className="flex items-center gap-1" data-testid={`classpass-studio-${i}`}>
                                  <MapPin className="w-3 h-3" />
                                  {pass.studio_name}
                                </span>
                              )
                            )}
                            {(pass.purchase_date || pass.purchased_at) && (
                              <span
                                className="flex items-center gap-1"
                                data-testid={`classpass-purchase-date-${i}`}
                              >
                                <CalendarDays className="w-3 h-3" />
                                Purchased {format(new Date(pass.purchase_date || pass.purchased_at), 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{pass.passes_remaining} of {pass.passes_total} passes remaining</span>
                            <span>{Math.round(100 - progress)}%</span>
                          </div>
                          <Progress value={100 - progress} className="h-2" />
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {neverExpires ? (
                            <p className="text-emerald-600 font-medium">Never Expires</p>
                          ) : (
                            <>
                              <p>Expires: {format(new Date(pass.expiry_date), 'MMM d, yyyy')}</p>
                              <p className="text-primary font-medium">{daysLeft} days left</p>
                            </>
                          )}
                        </div>
                        
                        {/* Auto-Renewal Toggle for Memberships */}
                        {isMembership && (
                          <div className="pt-2 border-t">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-4 h-4 text-muted-foreground" />
                                <Label htmlFor={`auto-renew-${pass.id}`} className="text-sm font-medium">
                                  Auto-Renew
                                </Label>
                              </div>
                              <Switch
                                id={`auto-renew-${pass.id}`}
                                checked={pass.auto_renew_enabled}
                                onCheckedChange={(checked) => handleToggleAutoRenew(pass, checked)}
                                disabled={processingAutoRenew}
                              />
                            </div>
                            {pass.auto_renew_enabled && (
                              <p className="text-xs text-emerald-600 mt-1">
                                Will renew automatically on expiry
                              </p>
                            )}
                            {!pass.auto_renew_enabled && savedCards.length === 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Add a payment method to enable
                              </p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Ticket className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No active ClassPasses</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Expired ClassPasses */}
        {expiredClassPasses.length > 0 && (
          <Card className="opacity-60">
            <CardHeader>
              <CardTitle>Expired/Used ClassPasses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {expiredClassPasses.slice(0, 5).map((pass, i) => (
                  <div key={pass.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div>
                      <p className="font-medium">{pass.class_pass_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {pass.passes_remaining}/{pass.passes_total} passes • {formatActivity(pass.activity_type)}
                      </p>
                    </div>
                    <Badge variant="secondary" className="capitalize">{pass.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Purchase New ClassPasses */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" />
              Purchase ClassPasses
            </CardTitle>
            <CardDescription>
              Buy ClassPasses from your favourite studios
            </CardDescription>
          </CardHeader>
          <CardContent>
            {availablePasses.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availablePasses.map((pass, i) => (
                  <Card key={pass.id} className="border hover:border-primary/50 transition-colors" data-testid={`available-pass-${i}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">{formatActivity(pass.activity_type)}</Badge>
                        <span className="text-lg font-bold text-primary">€{pass.price}</span>
                      </div>
                      <CardTitle className="text-base">{pass.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{pass.studio_name}</p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="text-sm space-y-1">
                        <p className="flex items-center justify-between">
                          <span className="text-muted-foreground">Credits:</span>
                          <span className="font-medium">{pass.passes_included} classes</span>
                        </p>
                        <p className="flex items-center justify-between">
                          <span className="text-muted-foreground">Validity:</span>
                          <span className="font-medium">
                            {(pass.validity_days === 0 || pass.validity_days >= 1825)
                              ? 'Never expires'
                              : `${pass.validity_days} days`}
                          </span>
                        </p>
                      </div>
                      {pass.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{pass.description}</p>
                      )}
                      <Button 
                        className="w-full" 
                        size="sm"
                        onClick={() => handlePurchaseClick(pass)}
                        data-testid={`purchase-pass-${i}-btn`}
                      >
                        <Ticket className="w-4 h-4 mr-2" />
                        {pass.is_membership ? 'Subscribe' : 'Purchase Pass'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Ticket className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground mb-2">No ClassPasses available</p>
                <p className="text-sm text-muted-foreground">Add studios to your favourites to see their available passes</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Saved Payment Methods Section - Always visible for clients to manage their cards */}
        <SavedPaymentMethods onCardAdded={handleCardAdded} />
      </div>

      {/* Auto-Renew Setup Dialog */}
      <Dialog open={showAutoRenewDialog} onOpenChange={setShowAutoRenewDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Set Up Auto-Renewal</DialogTitle>
            <DialogDescription>
              Add a payment method to enable automatic renewal of your membership
            </DialogDescription>
          </DialogHeader>
          <SavedPaymentMethods onCardAdded={handleCardAdded} />
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAutoRenewDialog(false);
              setAutoRenewPass(null);
            }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase ClassPass — shared <ClassPassPurchaseDialog /> handles
          voucher entry, /api/user-class-passes POST, and RyftPay 3DS flow. */}
      <ClassPassPurchaseDialog
        pass={purchasePass}
        onClose={() => setPurchasePass(null)}
        onPurchased={() => {
          setPurchasePass(null);
          fetchData();
        }}
      />

      {/* Iter-70: prompt to add a saved card before membership checkout. */}
      <Dialog open={!!membershipNeedsCard} onOpenChange={(o) => !o && setMembershipNeedsCard(null)}>
        <DialogContent className="sm:max-w-md" data-testid="membership-needs-card-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Save a card first
            </DialogTitle>
            <DialogDescription>
              Memberships auto-renew, so we need a card on file before you can
              subscribe. This keeps your access uninterrupted and lets you
              cancel any time.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 rounded-lg border bg-muted/40 text-sm">
            <p className="font-medium mb-2">{membershipNeedsCard?.name}</p>
            <p className="text-muted-foreground">
              €{membershipNeedsCard?.price} · {membershipNeedsCard?.studio_name}
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setMembershipNeedsCard(null)}
              data-testid="membership-needs-card-cancel"
            >
              Not now
            </Button>
            <Button
              onClick={() => {
                setMembershipNeedsCard(null);
                navigate('/billing');
              }}
              data-testid="membership-needs-card-goto-billing"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Go to Billing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
